import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import he from "he";
import { extractReportIdFromResponse, sendReport } from "../../functions/sendReport";
import {
  assetUrl,
  ensureThemeJsonArray,
  getStoredUser,
  getUserToken,
  isTrue,
  loopColor,
  themeColors,
} from "../../functions/themeAssets";
import "./game.css";

const KEY_ROWS = ["ABCDEFGHI", "JKLMNOPQ", "RSTUVWXYZ"];
const MAX_WRONG = 6;
const RESULT_KEY = "saveThemResult";
const SUBSCRIBE_TEXT = "Subscribe to any PLAN to play with your peers.";

const shuffle = (list) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const toTitleCase = (str) =>
  String(str || "")
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const pad = (n) => (n > 9 ? String(n) : `0${n}`);
const formatClock = (total) => `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
const isLetter = (ch) => /^[A-Za-z]$/.test(ch);

const decode = (s) => he.decode(String(s || ""));

function HourglassIcon() {
  return (
    <span className="hm-emoji-icon" role="img" aria-label="timer">
      ⏳
    </span>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path
        d="M12 2l3 6.6 7 .8-5.2 4.8 1.4 7.1L12 17.7l-6.2 3.6 1.4-7.1L2 9.4l7-.8z"
        fill="#FFCE31"
        stroke="#E8A400"
        strokeWidth="0.6"
      />
    </svg>
  );
}

function Hint({ q }) {
  if (!q.hintUrl) return null;
  if (q.hintType === "video") {
    return (
      <video key={q.hintUrl} className="show-image" controls>
        <source src={q.hintUrl} type="video/mp4" />
      </video>
    );
  }
  if (q.hintType === "audio") {
    return (
      <audio key={q.hintUrl} className="show-image" controls>
        <source src={q.hintUrl} type="audio/mpeg" />
      </audio>
    );
  }
  return <img src={q.hintUrl} className="show-image" alt="" />;
}

export default function GamePage() {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const token = getUserToken(user);
  const isDemo = user?.role === "demobypass" || String(user?.sessionId || "").startsWith("demo");

  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [index, setIndex] = useState(0);
  const [points, setPoints] = useState(0);
  const [used, setUsed] = useState(() => new Set());
  const [wrong, setWrong] = useState(0);
  // playing → guessing; won/lost → question resolved, waiting for popup / Next
  const [phase, setPhase] = useState("playing");
  const [lettersHidden, setLettersHidden] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [showRules, setShowRules] = useState(false);

  const secondsRef = useRef(0);
  const timerRef = useRef(null);
  const timeoutsRef = useRef([]);
  const submittingRef = useRef(false);
  // per-question outcomes so far (1 = won, 0 = lost), used to resume after a refresh
  const answersRef = useRef([]);

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timeoutsRef.current.push(t);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  // Periodic checkpoint so a refresh resumes instead of restarting. Demo sessions
  // aren't tracked server-side, so this is a no-op for them.
  const saveProgress = useCallback(() => {
    if (isDemo || !user) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/saveGameProgress`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userId: user.userId, answers: answersRef.current, elapsed: secondsRef.current }),
      keepalive: true,
    }).catch((err) => console.error("saveGameProgress failed", err));
  }, [isDemo, user, token]);

  // Catch the elapsed timer up to the server as the player navigates away.
  useEffect(() => {
    if (isDemo) return undefined;
    const onHide = () => {
      if (document.visibilityState === "hidden") saveProgress();
    };
    window.addEventListener("pagehide", saveProgress);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", saveProgress);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [isDemo, saveProgress]);

  // ---------- load ----------
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return undefined;
    }
    let cancelled = false;
    const timeouts = timeoutsRef.current;

    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getGameData`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            userId: user.userId,
            name: user.name,
            email: user.email,
            role: user.role || user.source,
          }),
        });
        const json = await res.json();
        if (cancelled) return;

        if (json.played) {
          navigate("/thankyou", { replace: true });
          return;
        }
        if (!json.success) throw new Error(json.message || "Could not load the game");

        const decodeQ = (q) => ({
          ...q,
          question_name: decode(q.question_name),
          correct_answer: decode(q.correct_answer),
        });

        let finalQuestions;
        const progress = json.progress;

        if (progress && Array.isArray(progress.questionIds) && progress.questionIds.length) {
          // the server already picked + persisted the play order — reuse it as-is so a
          // refresh resumes the same set instead of reshuffling
          const byId = new Map((json.questions || []).map((q) => [q.id, q]));
          finalQuestions = progress.questionIds.map((id) => byId.get(id)).filter(Boolean).map(decodeQ);
        } else {
          const list = (json.questions || []).map(decodeQ);
          const wanted = parseInt(json.theme.no_of_questions, 10) || list.length;
          if (list.length === 0 || wanted > list.length) {
            await Swal.fire("Please set a sufficient number of questions in the admin panel.", "", "error");
            navigate("/rules", { replace: true });
            return;
          }
          const ordered = isTrue(json.theme.shuffle_questions) ? shuffle(list) : list;
          finalQuestions = ordered.slice(0, wanted);
        }

        setTheme(json.theme);
        setQuestions(finalQuestions);

        if (progress) {
          answersRef.current = progress.answers || [];
          secondsRef.current = progress.elapsed || 0;
          setSeconds(secondsRef.current);
          setPoints(progress.points || 0);
          setIndex(answersRef.current.length);
        }

        setLoading(false);
        timerRef.current = setInterval(() => {
          secondsRef.current += 1;
          setSeconds(secondsRef.current);
          if (!isDemo && secondsRef.current % 10 === 0) saveProgress();
        }, 1000);
      } catch (err) {
        if (cancelled) return;
        await Swal.fire("Error", err.message || "Could not load the game", "error");
        navigate("/rules", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
      stopTimer();
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = questions[index];
  const answer = current ? current.correct_answer : "";
  const answerUpper = answer.toUpperCase();
  const perQuestion = parseInt(theme?.per_question_points, 10) || 0;
  const wrongPenalty = parseInt(theme?.wrong_question_points, 10) || 0;
  const colors = themeColors(theme);
  const questionLoopColor = loopColor(theme?.question_colors, index, colors.box);
  const selectedLoopColor = loopColor(theme?.selected_colors, index, colors.answer);
  const desktopBgUrl = assetUrl(theme?.background_desk);

  // letters that still have to be found (spaces and non A-Z characters are shown from the start)
  const isRevealed = (ch, guessed) => !isLetter(ch) || guessed.has(ch);

  // ---------- finishing ----------
  const submitResult = useCallback(
    async (finalPoints) => {
      if (submittingRef.current) return false;
      submittingRef.current = true;
      const time = formatClock(secondsRef.current);

      // external reports server (registered and guest players) — sent first so its
      // reportId can be stored alongside the score; non-fatal if it fails.
      let reportId = null;
      try {
        const reportRes = await sendReport({
          sessionId: user.sessionId,
          organizationId: user.organizationId,
          userId: user.userId,
          role: user.role || user.source,
          token: user.token,
          gameId: user.gameId,
          name: user.name || "",
          points: finalPoints,
          time: `00:${time}`,
        });
        reportId = extractReportIdFromResponse(reportRes);
      } catch (err) {
        console.error("Report push failed", err);
      }

      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/submitGame`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            userId: user.userId,
            name: user.name,
            email: user.email,
            role: user.role || user.source,
            points: finalPoints,
            time,
            reportId,
          }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.message || "something went wrong");
      } catch (err) {
        submittingRef.current = false;
        return false;
      }

      const total = questions.length * perQuestion;
      sessionStorage.setItem(RESULT_KEY, JSON.stringify({ points: finalPoints, total, time }));
      sessionStorage.setItem("userData", JSON.stringify({ ...user, gameover: 1 }));
      return true;
    },
    [user, token, questions.length, perQuestion]
  );

  const finishGame = useCallback(
    async (finalPoints, popup) => {
      stopTimer();
      let ok = await submitResult(finalPoints);
      while (!ok) {
        const retry = await Swal.fire({
          title: "Something went wrong",
          text: "Your score could not be saved.",
          icon: "error",
          confirmButtonText: "Try again",
        });
        if (!retry.isConfirmed) return;
        ok = await submitResult(finalPoints);
      }
      await Swal.fire({ title: popup.title, text: popup.text, background: popup.background });
      navigate("/thankyou", { replace: true });
    },
    [submitResult, navigate]
  );

  // ---------- one question resolved ----------
  const resolveQuestion = useCallback(
    (won, newPoints) => {
      const bgUrl = assetUrl(theme.background_desk);
      const popup = {
        title: won ? theme.correct_popup : theme.wrong_popup,
        text: toTitleCase(answer),
        background: bgUrl ? `#fff url("${bgUrl}") center / cover no-repeat` : undefined,
      };
      const isLast = index + 1 >= questions.length;

      if (!isDemo) {
        answersRef.current = [...answersRef.current, won ? 1 : 0];
        saveProgress();
      }

      const showPopup = async () => {
        setLettersHidden(true);
        if (isDemo) {
          // demo players only get the first question, then are pointed to the plans
          stopTimer();
          await Swal.fire({ title: popup.title, text: popup.text, background: popup.background });
          await Swal.fire({ title: SUBSCRIBE_TEXT, icon: "success" });
          window.location.href = `${process.env.REACT_APP_BASE_URL || ""}/plans`;
          return;
        }
        if (isLast) {
          await finishGame(newPoints, popup);
          return;
        }
        await Swal.fire({ title: popup.title, text: popup.text, background: popup.background });
        setShowNext(true);
      };

      if (won) {
        // let the player see the completed word for a moment
        later(showPopup, 1500);
      } else {
        setLettersHidden(true);
        showPopup();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, answer, index, questions.length, isDemo, finishGame, saveProgress]
  );

  // ---------- guessing ----------
  const guess = useCallback(
    (letter) => {
      if (phase !== "playing" || used.has(letter) || !current) return;

      const nextUsed = new Set(used).add(letter);
      setUsed(nextUsed);

      if (answerUpper.includes(letter)) {
        const solved = answerUpper.split("").every((ch) => isRevealed(ch, nextUsed));
        if (solved) {
          const newPoints = points + perQuestion;
          setPoints(newPoints);
          setPhase("won");
          resolveQuestion(true, newPoints);
        }
        return;
      }

      const nextWrong = wrong + 1;
      setWrong(nextWrong);
      if (nextWrong >= MAX_WRONG) {
        const newPoints = points - wrongPenalty;
        setPoints(newPoints);
        setPhase("lost");
        setWrong(0); // the original game resets the picture as soon as the round is lost
        resolveQuestion(false, newPoints);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phase, used, current, answerUpper, points, perQuestion, wrong, wrongPenalty, resolveQuestion]
  );

  // physical keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (showRules) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = String(e.key || "").toUpperCase();
      if (k.length === 1 && isLetter(k)) guess(k);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [guess, showRules]);

  const nextQuestion = () => {
    setIndex((i) => i + 1);
    setUsed(new Set());
    setWrong(0);
    setPhase("playing");
    setLettersHidden(false);
    setShowNext(false);
  };

  if (loading || !theme || !current) {
    return (
      <div className="hm-loader">
        <div className="hm-loader-spinner" />
      </div>
    );
  }

  const rules = ensureThemeJsonArray(theme.rules);
  const cssVars = { "--hm-box": colors.box, "--hm-text": colors.text, "--hm-answer": colors.answer };
  const hasText = current.question_name.replace(/\s+/g, "") !== "";

  return (
    <div className="hm-page" style={cssVars}>
      <div className="hm-topbar">
        <div className="hm-pill hm-timer">
          <span className="hm-pill-icon">
            <HourglassIcon />
          </span>
          <span className="hm-pill-text">{formatClock(seconds)}</span>
        </div>

        <div className="hm-ribbon-wrap">
          <div className="hm-ribbon" style={{ color: colors.text }}>
            {theme.landing_page_title}
          </div>
        </div>

        <div className="hm-topbar-right">
          <button type="button" className="hm-pill hm-rules-btn" onClick={() => setShowRules(true)}>
            Rules
          </button>
          <div className="hm-pill hm-points">
            <span className="hm-pill-icon">
              <StarIcon />
            </span>
            <span className="hm-pill-text">{points}</span>
          </div>
        </div>
      </div>

      <div className="hm-main">
        <div className="hm-left">
          {current.hintUrl && (
            <div className="hm-hint">
              <Hint q={current} />
            </div>
          )}

          {hasText && (
            <div className="hm-question-card" style={{ "--hm-box": questionLoopColor }}>
              <span className="hm-question-cluster">
                <span className="hm-question-mark hm-question-mark-tl">?</span>
                <span className="hm-question-badge">{index + 1}</span>
                <span className="hm-question-q">Q</span>
                <span className="hm-question-mark hm-question-mark-br">?</span>
              </span>
              <h4 className="hm-question" style={{ color: colors.text }}>
                {current.question_name}
              </h4>
            </div>
          )}

          <div className="hm-keyboard" style={{ "--hm-answer": selectedLoopColor }}>
            {KEY_ROWS.map((row) => (
              <div className="hm-keyrow" key={row}>
                {row.split("").map((letter) => {
                  const isUsed = used.has(letter);
                  return (
                    <button
                      key={letter}
                      type="button"
                      className={`hm-key${isUsed ? " used" : ""}`}
                      disabled={isUsed || phase !== "playing"}
                      onClick={() => guess(letter)}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <h4 className="hm-hintline" style={{ color: colors.text }}>
            Click on the keyboard to insert answer below
          </h4>

          {!lettersHidden && (
            <div className="hm-letters">
              {answerUpper.split("").map((ch, i) =>
                ch === " " ? (
                  <div key={i} className="hm-letter hm-space" />
                ) : (
                  <div key={i} className={`hm-letter${!isLetter(ch) || used.has(ch) ? " winner" : ""}`}>
                    {ch}
                  </div>
                )
              )}
            </div>
          )}

          {showNext && (
            <button type="button" className="hm-next" onClick={nextQuestion}>
              Next
            </button>
          )}
        </div>

        <div className="hm-right">
          <img
            className="hm-face"
            src={`${process.env.PUBLIC_URL || ""}/images/faces/${wrong}.png`}
            alt="hangman"
          />
        </div>
      </div>

      {showRules && (
        <div className="hm-modal" onClick={() => setShowRules(false)}>
          <div
            className="hm-modal-content"
            style={{
              "--hm-box": colors.box,
              ...(desktopBgUrl
                ? { backgroundImage: `url("${desktopBgUrl}")`, backgroundSize: "cover", backgroundPosition: "center" }
                : {}),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="hm-modal-header" style={{ color: colors.text }}>
              How to play
            </h3>
            <ul className="hm-modal-body">
              {rules.map((r, i) => (
                <li key={i} className="hm-modal-rule">
                  <span className="hm-modal-rule-tick">✓</span>
                  <span className="hm-modal-rule-text" style={{ color: colors.text }}>
                    {r}
                  </span>
                </li>
              ))}
            </ul>
            <div className="hm-modal-footer">
              <button type="button" className="hm-close" onClick={() => setShowRules(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
